/**
 * Typed API client for the V6 backend.
 *
 * All route helpers return properly-typed responses sourced from
 * `@memes/shared`. Thin wrappers around `fetch` with a shared error
 * handler.
 */
import type {
  AggregatedStats,
  AuthMeResponse,
  ApiError,
  BackupListResponse,
  DiscordStatus,
  GitSyncStatus,
  HalloweenState,
  LogLevel,
  MiscTracksResponse,
  NetworkMetricsDataPoint,
  NowPlaying,
  PlaybackSnapshot,
  QueueEntry,
  SessionLogEntry,
  SessionLogMeta,
  SessionLogSearchResult,
  YoutubeMusicAvailability,
  YoutubeMusicImportBody,
  YoutubeMusicJob,
  YoutubeMusicLinkBody,
  StatsPeriod,
  Track,
  VoiceChannelsResponse,
  VoiceStatus,
} from "@memes/shared";
import { devHeaders, withDevKeyQuery } from "./devKey";

const BASE = "/api/v1";

export class ApiRequestError extends Error {
  public readonly status: number;
  public readonly details?: unknown;
  constructor(status: number, message: string, details?: unknown) {
    super(message);
    this.name = "ApiRequestError";
    this.status = status;
    this.details = details;
  }
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  signal?: AbortSignal,
): Promise<T> {
  const headers = new Headers(init.headers);
  const hasBody = init.body != null && init.body !== "";
  if (hasBody && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  const res = await fetch(`${BASE}${path}`, {
    ...init,
    headers,
    signal,
    credentials: "include",
  });
  if (res.status === 204) return undefined as T;
  const data: unknown = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401 && typeof window !== "undefined") {
      const onLogin = window.location.pathname === "/login";
      if (!onLogin && path !== "/auth/me") {
        window.location.href = "/login";
      }
    }
    const err = data as ApiError;
    throw new ApiRequestError(res.status, err.message ?? err.error ?? res.statusText, err.details);
  }
  return data as T;
}

/* ─── Auth ─── */
export const authApi = {
  me: () => request<AuthMeResponse>("/auth/me"),
  logout: () => request<{ ok: true }>("/auth/logout", { method: "POST" }),
};

/* ─── Tracks ─── */
export const tracksApi = {
  list: () => request<Track[]>("/tracks"),
  remove: (id: number) => request<{ ok: true }>(`/tracks/${id}`, { method: "DELETE" }),
  removeByOwner: (owner: string) =>
    request<{ ok: true; count: number }>(
      `/tracks/by-owner/${encodeURIComponent(owner)}`,
      { method: "DELETE" },
    ),
  removeAll: () => request<void>("/tracks", { method: "DELETE" }),
  markPlayed: (id: number, ts?: number) =>
    request<{ ok: true; id: number; lastPlayed: number }>(
      `/tracks/${id}/played`,
      { method: "PATCH", body: JSON.stringify(ts != null ? { ts } : {}) },
    ),
  upload: async (owner: string, file: File): Promise<Track> => {
    const form = new FormData();
    form.append("owner", owner);
    form.append("file", file);
    const res = await fetch(`${BASE}/tracks`, { method: "POST", body: form });
    const data: unknown = await res.json().catch(() => ({}));
    if (!res.ok) {
      const err = data as ApiError;
      throw new ApiRequestError(res.status, err.message ?? err.error ?? res.statusText);
    }
    return data as Track;
  },
  coverUrl: (id: number) => `${BASE}/tracks/${id}/cover`,
};

/* ─── Queue ─── */
export const queueApi = {
  get: () => request<{ queue: QueueEntry[] }>("/queue"),
  put: (queue: { id: number; durationSec?: number }[], currentIndex = 0, currentTime = 0) =>
    request<{ ok: true; size: number }>("/queue", {
      method: "PUT",
      body: JSON.stringify({ queue, currentIndex, currentTime }),
    }),
  clear: () => request<{ ok: true }>("/queue", { method: "DELETE" }),
};

/* ─── Now playing ─── */
export const nowPlayingApi = {
  get: () => request<{ nowPlaying: NowPlaying | null }>("/now-playing"),
  set: (body: { id: number; index: number; queueLength: number; durationSec?: number }) =>
    request<{ ok: true; nowPlaying: NowPlaying }>("/now-playing", {
      method: "PUT",
      body: JSON.stringify(body),
    }),
  clear: (fullClear = false) =>
    request<{ ok: true }>(`/now-playing${fullClear ? "?fullClear=true" : ""}`, {
      method: "DELETE",
    }),
  resume: () => request<{ ok: true }>("/now-playing/resume", { method: "POST" }),
};

/* ─── Shared playback session ─── */
export const playbackApi = {
  get: () => request<PlaybackSnapshot>("/playback"),
  eventsUrl: () => `${BASE}/playback/events`,
  play: (index?: number) =>
    request<{ ok: true; snapshot: PlaybackSnapshot }>("/playback/play", {
      method: "POST",
      body: JSON.stringify(index != null ? { index } : {}),
    }),
  pause: (currentTime?: number) =>
    request<{ ok: true; snapshot: PlaybackSnapshot }>("/playback/pause", {
      method: "POST",
      body: JSON.stringify(
        currentTime != null ? { currentTime } : {},
      ),
    }),
  resume: () =>
    request<{ ok: true; snapshot: PlaybackSnapshot }>("/playback/resume", {
      method: "POST",
    }),
  seek: (currentTime: number) =>
    request<{ ok: true; snapshot: PlaybackSnapshot }>("/playback/seek", {
      method: "POST",
      body: JSON.stringify({ currentTime }),
    }),
  next: () =>
    request<{ ok: true; snapshot: PlaybackSnapshot }>("/playback/next", {
      method: "POST",
    }),
  prev: () =>
    request<{ ok: true; snapshot: PlaybackSnapshot }>("/playback/prev", {
      method: "POST",
    }),
  setActiveOwners: (activeOwners: Record<string, boolean>) =>
    request<{ ok: true; snapshot: PlaybackSnapshot }>("/playback/active-owners", {
      method: "PUT",
      body: JSON.stringify({ activeOwners }),
    }),
  settings: (normalization: { enabled?: boolean; preampDb?: number }) =>
    request<{ ok: true; snapshot: PlaybackSnapshot }>("/playback/settings", {
      method: "PUT",
      body: JSON.stringify({ normalization }),
    }),
  ended: (trackId: number, timelineRevision: number) =>
    request<{ ok: true; snapshot: PlaybackSnapshot }>("/playback/ended", {
      method: "POST",
      body: JSON.stringify({ trackId, timelineRevision }),
    }),
};

/* ─── Stats ─── */
export const statsApi = {
  period: (period: StatsPeriod) => request<AggregatedStats>(`/stats?period=${period}`),
  raw: () => request<unknown>("/stats/raw"),
  reportTime: (currentTime: number) =>
    request<{ ok: true }>("/stats/report-time", {
      method: "POST",
      body: JSON.stringify({ currentTime }),
    }),
  endSession: (listenedSeconds?: number) =>
    request<{ ok: true; listenedSeconds: number }>("/stats/end-session", {
      method: "POST",
      body: JSON.stringify(listenedSeconds != null ? { listenedSeconds } : {}),
    }),
  clear: () => request<{ ok: true }>("/stats", { method: "DELETE" }),
};

/* ─── Backups ─── */
export const backupsApi = {
  list: () => request<BackupListResponse>("/backups"),
  create: () => request<{ ok: true; filename: string | null }>("/backups", { method: "POST" }),
  restore: (filename: string) =>
    request<{ ok: true; trackCount: number; restoredLastPlayedCount: number }>(
      "/backups/restore",
      { method: "POST", body: JSON.stringify({ filename }) },
    ),
};

/* ─── Last played ─── */
export const lastPlayedApi = {
  get: () => request<{ count: number; entries: Record<string, number> }>("/last-played"),
  clear: () => request<{ ok: true }>("/last-played", { method: "DELETE" }),
};

/* ─── Halloween ─── */
export const halloweenApi = {
  get: () => request<HalloweenState>("/halloween"),
  set: (enabled: boolean) =>
    request<HalloweenState>("/halloween", { method: "PUT", body: JSON.stringify({ enabled }) }),
  misc: () => request<MiscTracksResponse>("/misc-tracks"),
};

/* ─── Owners ─── */
export const ownersApi = {
  list: () => request<{ owners: string[] }>("/owners"),
  set: (owners: string[]) =>
    request<{ owners: string[] }>("/owners", {
      method: "PUT",
      body: JSON.stringify({ owners }),
    }),
};

async function devRequest<T>(
  path: string,
  init: RequestInit = {},
  signal?: AbortSignal,
): Promise<T> {
  const headers = new Headers(init.headers);
  for (const [k, v] of Object.entries(devHeaders())) {
    if (typeof v === "string") headers.set(k, v);
  }
  return request<T>(path, { ...init, headers }, signal);
}

/* ─── Dev ─── */
export interface LoudnessScanJobDto {
  status: "idle" | "running" | "completed" | "failed";
  skipProcessed: boolean;
  startedAt: number | null;
  completedAt: number | null;
  progress: {
    current: number;
    total: number;
    updated: number;
    skipped: number;
    trackTitle: string | null;
  } | null;
  result: { scanned: number; updated: number; skipped: number } | null;
  error: string | null;
}

export const devApi = {
  randomizePlayed: () =>
    devRequest<{ ok: true; count: number }>("/dev/randomize-played", { method: "POST" }),
  clearPlayed: () =>
    devRequest<{ ok: true; count: number }>("/dev/clear-played", { method: "POST" }),
  refreshMetadata: () =>
    devRequest<{ ok: true; success: number; fail: number }>("/dev/refresh-metadata", {
      method: "POST",
    }),
  backfillMetadataKeys: () =>
    devRequest<{ ok: true; backfilled: number }>("/dev/backfill-metadata-keys", {
      method: "POST",
    }),
  scanLoudness: (skipProcessed?: boolean) =>
    devRequest<{ ok: true; job: LoudnessScanJobDto }>(
      `/dev/scan-loudness${skipProcessed ? "?skipProcessed=true" : ""}`,
      { method: "POST" },
    ),
  loudnessScanStatus: () =>
    devRequest<{ job: LoudnessScanJobDto }>("/dev/scan-loudness"),
  /** Subscribe to the existing server-owned scan job. */
  loudnessScanStreamUrl: () =>
    withDevKeyQuery(`${BASE}/dev/scan-loudness/stream`),
};

export const devLogsApi = {
  listSessions: () => devRequest<{ sessions: SessionLogMeta[] }>("/dev/logs/sessions"),
  searchSession: (
    sessionId: string,
    params: {
      q?: string;
      level?: LogLevel;
      errorsOnly?: boolean;
      module?: string;
      source?: "server" | "client";
      clientId?: string;
      offset?: number;
      limit?: number;
    } = {},
  ) => {
    const qs = new URLSearchParams();
    if (params.q) qs.set("q", params.q);
    if (params.errorsOnly) qs.set("errorsOnly", "true");
    if (params.level) qs.set("level", params.level);
    if (params.module) qs.set("module", params.module);
    if (params.source) qs.set("source", params.source);
    if (params.clientId) qs.set("clientId", params.clientId);
    if (params.offset != null) qs.set("offset", String(params.offset));
    if (params.limit != null) qs.set("limit", String(params.limit));
    const query = qs.toString();
    return devRequest<SessionLogSearchResult>(
      `/dev/logs/sessions/${encodeURIComponent(sessionId)}${query ? `?${query}` : ""}`,
    );
  },
  deleteSession: (sessionId: string) =>
    devRequest<{ ok: true }>(`/dev/logs/sessions/${encodeURIComponent(sessionId)}`, {
      method: "DELETE",
    }),
  liveStreamUrl: () => withDevKeyQuery(`${BASE}/dev/logs/live/stream`),
};

export type { SessionLogEntry };

/* ─── Git backup ─── */
export const gitSyncApi = {
  status: () => request<GitSyncStatus>("/git-sync/status"),
  trigger: () => request<{ ok: true; syncing: boolean; message: string }>("/git-sync", {
    method: "POST",
  }),
  restore: () =>
    request<{ ok: true; syncing: boolean; message: string }>("/git-sync/restore", {
      method: "POST",
    }),
  forcePush: () =>
    request<{ ok: true; syncing: boolean; message: string }>("/git-sync/force-push", {
      method: "POST",
    }),
  /** @deprecated Prefer restore() */
  forceResync: () =>
    request<{ ok: true; added: number; restoredLastPlayedCount: number }>(
      "/git-sync/force-resync",
      { method: "POST" },
    ),
  eventsUrl: () => `${BASE}/git-sync/events`,
};

/* ─── Lyrics ─── */
export const lyricsApi = {
  fetch: (artist: string, title: string) =>
    request<{ lyrics: string; source: string; url: string }>(
      `/lyrics?artist=${encodeURIComponent(artist)}&title=${encodeURIComponent(title)}`,
    ),
};

/* ─── Discord ─── */
export const discordApi = {
  status: () => request<DiscordStatus>("/discord/status"),
  setChannel: (channelId: string) =>
    request<{ ok: true; channelId: string | null }>("/discord/channel", {
      method: "PUT",
      body: JSON.stringify({ channelId }),
    }),
  voiceChannels: () => request<VoiceChannelsResponse>("/discord/voice/channels"),
  voiceJoin: (channelId: string, guildId: string, trackId?: number) =>
    request<{ ok: true; channelId: string; guildName: string }>("/discord/voice/join", {
      method: "POST",
      body: JSON.stringify({
        channelId,
        guildId,
        ...(trackId != null ? { trackId } : {}),
      }),
    }),
  voiceLeave: () => request<{ ok: true }>("/discord/voice/leave", { method: "POST" }),
  voiceStatus: () => request<VoiceStatus>("/discord/voice/status"),
  voicePause: () => request<{ ok: true }>("/discord/voice/pause", { method: "POST" }),
  voiceResume: (trackId?: number) =>
    request<{ ok: true }>("/discord/voice/resume", {
      method: "POST",
      body: JSON.stringify(trackId != null ? { trackId } : {}),
    }),
  voiceStop: () => request<{ ok: true }>("/discord/voice/stop", { method: "POST" }),
  voiceVolume: (volume: number, normalize?: boolean) =>
    request<{ ok: true }>("/discord/voice/volume", {
      method: "PUT",
      body: JSON.stringify({
        volume,
        ...(normalize !== undefined ? { normalize } : {}),
      }),
    }),
  voiceSeek: (seconds: number) =>
    request<{ ok: true }>("/discord/voice/seek", {
      method: "POST",
      body: JSON.stringify({ seconds }),
    }),
  networkStats: () =>
    request<{ connected: boolean; history: NetworkMetricsDataPoint[] }>(
      "/discord/voice/network-stats",
    ),
};

/* ─── YouTube Music ─── */
export const youtubeMusicApi = {
  availability: () =>
    request<YoutubeMusicAvailability>("/youtube-music/availability"),
  refreshAvailability: () =>
    request<YoutubeMusicAvailability>("/youtube-music/availability/refresh", {
      method: "POST",
    }),
  importJobs: () => request<{ jobs: YoutubeMusicJob[] }>("/youtube-music/import"),
  importJob: (id: string) => request<YoutubeMusicJob>(`/youtube-music/import/${id}`),
  importUrl: (body: YoutubeMusicImportBody) =>
    request<{ ok: true; job: YoutubeMusicJob }>("/youtube-music/import", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  link: (body: YoutubeMusicLinkBody) =>
    request<{ ok: true; track: Track }>("/youtube-music/link", {
      method: "POST",
      body: JSON.stringify(body),
    }),
  eventsUrl: (id: string) => `${BASE}/youtube-music/import/${id}/events`,
};

/* ─── Health ─── */
export const healthApi = {
  check: () =>
    request<{
      ok: true;
      version: string;
      uptimeSec: number;
      readiness: Record<string, boolean>;
    }>("/health"),
};
